<template>
  <div :class="{ 'view-content': true, 'fullscreen-editor': fullscreen }">
<!-- sticky header -->
<div class="sticky-header">
  <div class="d-flex align-center justify-space-between">
    <!-- title on the left -->
    <h2 class="view-title">Attribute Editor</h2>

    <!-- controls on the right -->
    <div class="d-flex align-center gap-4">
      <span>sort by:</span>
      <v-select
        dense
        style="width: 160px"
        :items="['display_name', 'attribute_id']"
        :model-value="sortBy"
        @update:modelValue="$emit('update:sortBy', $event)"
      />
      <v-btn icon @click="$emit('toggleFull')">
        <v-icon>{{ fullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
      </v-btn>
    </div>
  </div>

    <div class="d-flex gap-4 mt-2">
      <v-btn color="primary"    @click="$emit('newAttr')">create new attribute</v-btn>
      <v-btn color="secondary"  @click="$emit('exportCSV')">export csv</v-btn>
      <v-btn color="secondary"  @click="$emit('recheckCats')">recheck categories</v-btn>
    </div>
  </div>

  <!-- the massive table -->
  <v-data-table
    :headers="headers"
    :items="items"
    item-key="attribute_id"
    :items-per-page="15"
    fixed-header class="elevated"
    :height="tableHeight"
    :footer-props="footerProps"
  >
    <!-- ID -->
    <template #item.attribute_id="{item}">
      <span>{{ item.attribute_id }}</span>
    </template>

    <!-- Name -->
    <template #item.name="{item}">
      <a @click="openText(item,'name')">{{ item.name || '(none)' }}</a>
    </template>

    <!-- display name -->
    <template #item.display_name="{item}">
      <a @click="openText(item,'display_name')">
        {{ getDisplayName(item) }}
      </a>
    </template>

    <!-- description -->
    <template #item.description="{item}">
      <div class="wrap-cell"
           @click="openText(item,'description')"
           title="click to edit">
        {{ item.description || '(none)' }}
      </div>
    </template>

    <!-- values column (drag-n-drop chips) -->
    <template #item.values="{item}">
      <!-- THE FIX: Add a wrapper div with a class and apply a min-width in CSS.
           This tells the table layout to give this column more space. -->
      <div class="values-column-wrapper">
        <draggable tag="div"
          :list="item.valuesArray"
          :group="'attrValues-'+item.attribute_id"
          item-key="value_en"
          class="d-flex flex-wrap gap-1"
          @end="updateItemValues(item)">
          <template #item="{element,index}">
            <v-chip v-if="shouldDisplayValue(item,index)"
                    class="value-chip" small
                    :style="getAttrValueChipStyle(item.attribute_id,element.value_en)"
                    @contextmenu.prevent="showValueMenu($event,item.attribute_id,element.value_en)">
              {{ element.value_en }}
              <v-icon small class="delete-icon"
                      @click.stop="removeAttrVal(item,index)">mdi-close</v-icon>
            </v-chip>
          </template>
        </draggable>

        <div v-if="item.valuesArray.length>7" class="mt-1">
          <v-btn text small @click="toggleExpandVals(item)">
            {{ expandedVals[item.attribute_id]?'collapse':'expand' }}
          </v-btn>
        </div>

        <div class="d-flex align-center mt-2 gap-2">
          <v-text-field hide-details dense style="max-width:120px"
            v-model="newVal[item.attribute_id]"
            placeholder="add value"
            @keydown.enter="addAttrVal(item)"/>
          <v-icon small @click="addAttrVal(item)" style="cursor:pointer">mdi-plus</v-icon>
        </div>
      </div>
    </template>

    <!-- attribute_group -->
    <template #item.attribute_group="{item}">
      <a @click="openText(item,'attribute_group')">
        {{ item.attribute_group || '(none)' }}
      </a>
    </template>

    <!-- data type -->
    <template #item.data_type="{item}">
      <v-select dense hide-details style="width:140px"
        :items="['integer','float','text','boolean']"
        v-model="item.data_type"/>
    </template>

<!-- booleans group 1 -->
<template #item.booleans_group_1="{ item }">
<div class="bool-col">
<div class="switch-stack">
  <v-switch v-model="item.always_required"
            density="compact" hide-details
            :color="item.always_required ? 'success' : 'grey'" />
  <span class="switch-label">always required</span>
</div>

<div class="switch-stack">
  <v-switch v-model="item.is_image_attribute"
            density="compact" hide-details
            :color="item.is_image_attribute ? 'success' : 'grey'" />
  <span class="switch-label">is image attr</span>
</div>

<div class="switch-stack">
  <v-switch v-model="item.is_closed_list"
            density="compact" hide-details
            :color="item.is_closed_list ? 'success' : 'grey'" />
  <span class="switch-label">is closed list</span>
</div>
</div>
</template>


<!-- booleans group 2 -->
<template #item.booleans_group_2="{ item }">
<div class="bool-col">
<div class="switch-stack">
  <v-switch v-model="item.filter_attribute"
            density="compact" hide-details
            :color="item.filter_attribute ? 'success' : 'grey'" />
  <span class="switch-label">filter attribute</span>
</div>

<div class="switch-stack">
  <v-switch v-model="item.critical_attribute"
            density="compact" hide-details
            :color="item.critical_attribute ? 'success' : 'grey'" />
  <span class="switch-label">critical attribute</span>
</div>
</div>
</template>

<!-- booleans group 3 -->
<template #item.booleans_group_3="{ item }">
<div class="bool-col">
<div class="switch-stack">
  <v-switch v-model="item.secondary_allowed"
            density="compact" hide-details
            :color="item.secondary_allowed ? 'success' : 'grey'" />
  <span class="switch-label">secondary allowed</span>
</div>

<div class="switch-stack">
  <v-switch v-model="item.customer_facing"
            density="compact" hide-details
            :color="item.customer_facing ? 'success' : 'grey'" />
  <span class="switch-label">customer facing</span>
</div>

<div class="switch-stack">
  <v-switch v-model="item.is_category_type_attribute"
            density="compact" hide-details
            :color="item.is_category_type_attribute ? 'success' : 'grey'" />
  <span class="switch-label">category type attr</span>
</div>
</div>
</template>



    <!-- sort order -->
    <template #item.sort_order="{item}">
      <v-select dense hide-details style="width:140px"
        :items="['None','Alphabetical','Value Sort','Custom Sort']"
        v-model="item.sort_order"/>
    </template>

    <!-- attribute level -->
    <template #item.attribute_level="{item}">
      <v-select dense hide-details style="width:140px"
        :items="['Product','Variant']"
        v-model="item.attribute_level"/>
    </template>

    <!-- attribute unit -->
    <template #item.attribute_unit="{item}">
      <v-text-field dense hide-details style="width:120px"
        v-model="item.attribute_unit"/>
    </template>

    <!-- notes -->
    <template #item.notes="{item}">
      <div class="wrap-cell" title="click to edit"
           @click="openText(item,'notes')">
        {{ item.notes || '(none)' }}
      </div>
    </template>

    <!-- filter_sources -->
    <template #item.filter_sources="{item}">
      <div class="d-flex flex-wrap gap-2">
        <template v-if="item.filter_sources && item.filter_sources.length">
          <v-chip v-for="(src,i) in item.filter_sources" :key="i"
                  class="value-chip" small
                  :style="getSourceChipStyle(src)">
            {{ src }}
          </v-chip>
        </template>
        <template v-else>
          <span class="muted">(none)</span>
        </template>
      </div>
    </template>

<!-- categories -->
<template #item.categories="{item}">
<div class="cat-col">
<div class="d-flex flex-wrap gap-2">
  <!-- Show "(none)" if there are no categories -->
  <template v-if="!item.categories || !item.categories.length">
    <span class="muted">(none)</span>
  </template>
  
  <!-- Otherwise, loop through and display them -->
  <template v-else>
    <!--
      THE FIX: Use a <template> tag for the v-for loop.
      This allows the v-if to be on a separate, inner element,
      which is the correct pattern for Vue 3.
    -->
    <template v-for="(cat, i) in item.categories" :key="i">
      <v-chip v-if="showCat(item, i)"
              small class="value-chip" color="#e0f7fa" style="color:#000">
        {{ cat }}
      </v-chip>
    </template>
  </template>
</div>

<!-- The expand/collapse button (this part was already correct) -->
<div v-if="item.categories && item.categories.length > 7" class="mt-1">
  <v-btn text small @click="$emit('toggleExpandCats', item)">
    {{ expandedCats[item.attribute_id] ? 'collapse' : 'expand' }}
  </v-btn>
</div>
</div>
</template>

    <!-- delete action -->
    <template #item.actions="{item}">
      <v-btn icon color="error" @click="$emit('deleteAttr',item)">
        <v-icon>mdi-delete</v-icon>
      </v-btn>
    </template>
  </v-data-table>
</div>
</template>

<script setup>
import Draggable from 'vuedraggable'
import { ref } from 'vue'

const props = defineProps({
headers: Array,
items: Array,
tableHeight: String,
footerProps: Object,
sortBy: String,
fullscreen: Boolean,
getDisplayName: Function,
getValuesArray: Function,
shouldDisplayValue: Function,
getAttrValueChipStyle: Function,
removeAttrVal: Function,
addAttrVal: Function,
toggleExpandVals: Function,
expandedVals: Object,
newVal: Object,
openTextEditDialog: Function,
updateItemValues: Function,
getSourceChipStyle: Function,
showCat: Function,
expandedCats: Object,
showValueContextMenu: Function,
renameValueFromContextMenu: Function,
addNotesFromContextMenu: Function
})

const emit = defineEmits(['update:sortBy', 'toggleFull', 'newAttr', 'exportCSV', 'recheckCats', 'deleteAttr', 'toggleExpandCats'])

const valueContextMenu = ref({
show: false,
x: 0,
y: 0,
attrId: null,
value: null
})

// Helper functions
function openText(item, field) {
props.openTextEditDialog(item, field)
}

function showValueMenu(evt, id, val) {
evt.preventDefault();
if (props.showValueContextMenu) {
props.showValueContextMenu(evt, id, val);
}
}

// Wrapper functions for props
function getDisplayName(item) {
return props.getDisplayName ? props.getDisplayName(item) : item.display_name?.en || item.name || ''
}

function shouldDisplayValue(item, index) {
return props.shouldDisplayValue ? props.shouldDisplayValue(item, index) : index < 7
}

function getAttrValueChipStyle(attrId, value) {
return props.getAttrValueChipStyle ? props.getAttrValueChipStyle(attrId, value) : {}
}

function removeAttrVal(item, index) {
if (props.removeAttrVal) {
  props.removeAttrVal(item, index)
}
}

function addAttrVal(item) {
if (props.addAttrVal) {
  props.addAttrVal(item)
}
}

function toggleExpandVals(item) {
if (props.toggleExpandVals) {
  props.toggleExpandVals(item)
}
}

function updateItemValues(item) {
if (props.updateItemValues) {
  props.updateItemValues(item)
}
}

function getSourceChipStyle(src) {
return props.getSourceChipStyle ? props.getSourceChipStyle(src) : {}
}

function showCat(item, index) {
return props.showCat ? props.showCat(item, index) : index < 5
}
</script>

<style scoped>
.wrap-cell {
white-space: pre-wrap;
overflow-wrap: anywhere;
cursor: pointer;
/* Also give these text columns a reasonable minimum width */
min-width: 200px;
}
.muted{font-size:.9rem;color:#666}
.cat-col{display:flex;flex-direction:column;gap:.25rem}

/* --- THE FIX --- */
/* This new rule targets the wrapper div we added in the template. */
/* By giving the content a minimum width, we force the table cell to expand, */
/* preventing the chips from wrapping too aggressively. */
.values-column-wrapper {
min-width: 350px;
}

.value-chip .delete-icon{display:none}
.value-chip:hover .delete-icon{display:inline-block}
.fullscreen-editor{position:fixed;top:0;left:0;width:100vw;height:100vh;z-index:3000;background:#fff;padding:1rem;box-sizing:border-box}
.bool-row{
display:flex;
align-items:center;
gap:0.5rem;      /* space between switch and text */
line-height:1;   /* keeps label vertically centred */
}

.bool-col {
display: flex;
flex-direction: column;
gap: 1rem;
align-items: flex-start;
}

.switch-stack {
display: flex;
flex-direction: column;
align-items: center;
width: 90px;
}

.switch-label {
font-size: 0.75rem;
text-align: center;
}
</style>